import React, { useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUpRight, ArrowRight } from "lucide-react";
import DiscoveryDeployment from "./DiscoveryDeployment";
import InvestorMolecularVisual from "./InvestorMolecularVisual";
import InvestorProgramVisual from "./InvestorProgramVisual";
import { COMPANY_WORK, DISCOVERY_LINK, INVESTOR_LINKS } from "../../content/siteContent";
import CompanyLeadership from "./CompanyLeadership";
import "./InvestorStory.css";

const researchInterests = [
  { id: "rna", label: "RNA delivery", title: "Beyond the liver.", question: "How can siRNA reach the intended cells beyond the liver and silence the target gene?" },
  { id: "proteins", label: "Fusion proteins", title: "New functions, by design.", question: "Which combinations of protein domains could create useful therapeutic activity?" },
  { id: "biology", label: "Multimodal biology", title: "Find the mechanism.", question: "Which drug targets are supported by evidence across DNA, RNA, protein and chromatin?" },
];

const productAgents = [
  { name: "Atlas", role: "Computational biology", id: "computational_biologist", image: "atlas-comp-bio.png", width: 602, height: 435 },
  { name: "Orin", role: "Assay design", id: "protocol_designer", image: "orin-protocol.png", width: 256, height: 256 },
  { name: "Sigma", role: "Statistics", id: "statistician", image: "sigma-stats.png", width: 500, height: 500 },
];

function wave(y, phase = 0, amplitude = 15) {
  return Array.from({ length: 65 }, (_, i) => `${i ? "L" : "M"}${90 + i * 5.5},${y + Math.sin(i * .28 + phase) * amplitude}`).join(" ");
}

function RnaStudy() {
  return <svg viewBox="0 0 600 380" role="img" aria-label="Conceptual RNA delivery study: RNA outside a target cell, with a proposed route across its membrane.">
    <circle className="study-cell-fill" cx="402" cy="184" r="136" />
    {[128, 138].map(r => <circle className="study-line study-membrane" key={r} cx="402" cy="184" r={r} />)}
    {Array.from({ length: 72 }, (_, i) => { const a = i / 72 * Math.PI * 2; return <path className="study-membrane-tick" key={i} d={`M${402 + Math.cos(a) * 128} ${184 + Math.sin(a) * 128}L${402 + Math.cos(a) * 138} ${184 + Math.sin(a) * 138}`} />; })}
    <g className="study-payload">
      <circle cx="132" cy="184" r="48" />
      <path d="M110 162c43-9 42 24 7 16s-17 28 14 17 30 20-12 15" />
    </g>
    <path className="study-proposed-route" d="M198 184h58m38 0h48" />
    <path className="study-route-tip" d="m334 178 8 6-8 6" />
    <path className="study-rna-in-cell" d="M383 163c43-9 42 24 7 16s-17 28 14 17 30 20-12 15" />
    <path className="study-label-rule" d="M132 248v32M402 329v13" />
    <text x="132" y="305" textAnchor="middle">RNA</text>
    <text x="402" y="365" textAnchor="middle">Target cell</text>
  </svg>;
}

function FusionStudy() {
  return <svg viewBox="0 0 600 380" role="img" aria-label="Conceptual engineered fusion protein: two protein domains joined by a linker, with their combined function to be tested.">
    <g className="study-domain study-domain-a" transform="translate(170 175)">
      {Array.from({ length: 9 }, (_, i) => <ellipse key={i} rx={68 + Math.sin(i) * 6} ry="33" transform={`rotate(${i * 20})`} />)}
      <circle r="9" />
    </g>
    <path className="study-linker" d="M244 175c17-30 17 30 34 0s17 30 34 0 17 30 34 0" />
    <g className="study-domain study-domain-b" transform="translate(420 175)">
      {Array.from({ length: 9 }, (_, i) => <ellipse key={i} rx="63" ry={26 + Math.sin(i) * 5} transform={`rotate(${i * 20 + 10})`} />)}
      <circle r="9" />
    </g>
    <path className="study-label-rule" d="M170 260v38M295 198v100M420 255v43" />
    <text x="170" y="324" textAnchor="middle">Domain A</text>
    <text x="295" y="324" textAnchor="middle">Linker</text>
    <text x="420" y="324" textAnchor="middle">Domain B</text>
  </svg>;
}

function MultimodalStudy() {
  return <svg viewBox="0 0 600 380" role="img" aria-label="Conceptual multimodal biology study connecting DNA, RNA, protein and chromatin information. These curves are schematics, not measured data.">
    <g className="study-omics-links"><path d="M120 68 205 148 300 228 425 308M340 68 420 148 250 228 170 308M430 68 310 148 380 228 310 308" /></g>
    {[68, 148, 228, 308].map((y, row) => <g key={y} className={`study-omics-row study-omics-${row}`}>
      <path d={wave(y, row, row === 2 ? 24 : 13)} />
      {row === 0 && <path d={wave(y, Math.PI, 13)} />}
      {Array.from({ length: 17 }, (_, i) => <circle key={i} cx={90 + i * 22} cy={y + Math.sin(i * 4 * .28 + row) * (row === 2 ? 24 : 13)} r={row === 3 ? 9 : 3.5} />)}
    </g>)}
    {["DNA", "RNA", "Protein", "Chromatin"].map((label, i) => <text key={label} x="570" y={73 + i * 80} textAnchor="end">{label}</text>)}
  </svg>;
}

export function InvestorResearchInterests() {
  const [selected, setSelected] = useState(0);
  const tabs = useRef([]);
  const id = useId();
  const interest = researchInterests[selected];
  function onKeys(event) {
    let next;
    if (event.key === "ArrowRight") next = (selected + 1) % researchInterests.length;
    if (event.key === "ArrowLeft") next = (selected + researchInterests.length - 1) % researchInterests.length;
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = researchInterests.length - 1;
    if (next === undefined) return;
    event.preventDefault();
    setSelected(next);
    tabs.current[next]?.focus();
  }
  return <section className="investor-frontiers" aria-label="Research interests">
    <div className="investor-interest-tabs" role="tablist" aria-label="Research interests" onKeyDown={onKeys}>
      {researchInterests.map((item, index) => <button key={item.id} type="button" role="tab" id={`${id}-${item.id}`} aria-controls={`${id}-panel`} aria-selected={index === selected} tabIndex={index === selected ? 0 : -1} ref={el => { tabs.current[index] = el; }} onClick={() => setSelected(index)}>{item.label}<ArrowUpRight size={17} aria-hidden="true" /></button>)}
    </div>
    <div className="investor-interest-panel" role="tabpanel" id={`${id}-panel`} aria-labelledby={`${id}-${interest.id}`} tabIndex={0}>
      <div className="investor-interest-copy" key={`${interest.id}-copy`}><h3>{interest.title}</h3><p>{interest.question}</p></div>
      <div className="investor-interest-study" key={interest.id}>{selected === 0 ? <RnaStudy /> : selected === 1 ? <FusionStudy /> : <MultimodalStudy />}</div>
    </div>
  </section>;
}

function Vision() {
  return <section className="journey-shell investor-overview" id="vision" data-investor-section="vision" aria-labelledby="investor-title">
    <div className="investor-overview-copy">
      <h1 id="investor-title"><span>The medicines</span> <span>we’re still</span> <em>waiting for.</em></h1>
      <p>We’re building a drug company around scientific AI, partner expertise and a stake in the medicines we help develop.</p>
      <Link className="investor-story-start" to={INVESTOR_LINKS.development.to}>{INVESTOR_LINKS.development.label} <ArrowDown size={17} aria-hidden="true" /></Link>
    </div>
    <InvestorMolecularVisual className="investor-hero-art" />
  </section>;
}

function OurWork() {
  const [showResearchModel, setShowResearchModel] = useState(false);
  return <section className="investor-work journey-shell" id="why-now" data-investor-section="why-now" aria-labelledby="investor-work-title">
    <h2 className="journey-title" id="investor-work-title">Research systems, <em>already at work.</em></h2>
    <div className="investor-work-spread">
      <dl className="investor-projects">
        {COMPANY_WORK.map(({ id, name, work, source }) => <div key={id}><dt>{source ? <a href={source} target="_blank" rel="noreferrer">{name} <ArrowUpRight size={17} aria-hidden="true" /></a> : name}</dt><dd>{work}</dd></div>)}
      </dl>
      <figure className="investor-product">
        <img className="investor-product-screen" src="/images/discovery/team-workspace.png" alt="Cornucopia Discovery: the Full Lab Team workspace, with Aster routing questions to specialist research agents." width="1810" height="1024" loading="lazy" decoding="async" />
        <ul className="investor-product-agents" aria-label="Discovery research agents">
          {productAgents.map(agent => <li key={agent.id}>
            <a href={`https://discovery.cornucopiabio.com/agents/${agent.id}`} target="_blank" rel="noreferrer" aria-label={`Meet ${agent.name}, ${agent.role}. Opens Discovery in a new tab; sign-in may be required.`}>
              <span className={`investor-product-portrait${agent.width > agent.height ? " investor-product-portrait-wide" : ""}`}><img src={`/images/discovery/agents/${agent.image}`} alt="" width={agent.width} height={agent.height} loading="lazy" /></span>
              <strong>{agent.name}</strong><span>{agent.role}</span>
            </a>
          </li>)}
        </ul>
        <figcaption><span>Discovery</span><a href={DISCOVERY_LINK.href} target="_blank" rel="noreferrer" aria-label="Explore Discovery. Opens a new tab; sign-in may be required.">{DISCOVERY_LINK.label} <ArrowUpRight size={17} aria-hidden="true" /></a></figcaption>
      </figure>
    </div>
    <details className="investor-disclosure investor-research-model" onToggle={event => setShowResearchModel(event.currentTarget.open)}>
      <summary>Explore the deployment model <span aria-hidden="true">+</span></summary>
      {showResearchModel && <DiscoveryDeployment perspective="partnership" />}
    </details>
  </section>;
}

function Development() {
  return <section className="investor-program-development" id="inside-the-lab" data-investor-section="inside-the-lab" aria-labelledby="investor-development-title">
    <div className="journey-shell">
      <div className="investor-development-heading">
        <h2 className="journey-title" id="investor-development-title">Develop the medicine.<br /><em>Share in its future.</em></h2>
      </div>
      <InvestorProgramVisual />
      <details className="investor-disclosure investor-research-interests">
        <summary>Research interests <span aria-hidden="true">+</span></summary>
        <InvestorResearchInterests />
      </details>
    </div>
  </section>;
}

function TheGoal() {
  return <section className="investor-purpose" id="get-in-touch" data-investor-section="our-team" aria-labelledby="investor-purpose-title">
    <div className="journey-shell">
      <h2 id="investor-purpose-title">Every life is worth developing <em>a medicine for.</em></h2>
      <Link className="journey-button investor-contact" to={`${INVESTOR_LINKS.contact.to}?source=investor-story`}>{INVESTOR_LINKS.contact.label} <ArrowRight size={20} aria-hidden="true" /></Link>
    </div>
  </section>;
}

export default function InvestorStory() {
  return <div className="investor-story investor-page"><Vision /><Development /><OurWork /><CompanyLeadership /><TheGoal /></div>;
}
